/**
 * User Service - Smart Database Selection
 * Tự động chọn backend phù hợp (WordPress, Python hoặc SQLite)
 */

import { User, UserProfile } from '../../types/auth';
import { wordPressUserService } from '../api/WPUserService';
import { sqliteDatabaseService } from './SQLiteDatabaseService';
import { databaseConfig } from '../../config/database.config';
import { API_CONFIG } from '../../config/api.config';
import { pythonUserService } from '../api/PythonUserService';

class UserService {
  private baseUrl = '/api/users';
  private useWordPress: boolean;
  private usePythonBackend: boolean; // ✅ New flag
  private wordPressHealthy: boolean = true;
  private lastHealthCheck: number = 0;
  private healthCheckInterval: number = 60000;
  private enableLocalFallback: boolean = false;

  constructor() {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

    this.enableLocalFallback =
      import.meta.env.VITE_ENABLE_LOCAL_FALLBACK === 'true' ||
      localStorage.getItem('enable_fallback_debug') === 'true';

    // ✅ Check Python Backend Flag
    this.usePythonBackend = API_CONFIG.features.usePythonBackend;

    // Auto-select backend
    this.useWordPress = databaseConfig.backend === 'wordpress' && !this.usePythonBackend;

    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🔧 UserService Initialization');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📊 Config:', {
      usePythonBackend: this.usePythonBackend,
      useWordPress: this.useWordPress,
      backend: databaseConfig.backend,
      apiUrl: this.usePythonBackend ? API_CONFIG.python.apiUrl : databaseConfig.wordpressApiUrl,
      mode: import.meta.env.MODE,
    });
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    // Only check WP health if we are using it
    if (this.useWordPress) {
      console.log('🏥 Starting WordPress health check...');
      this.checkWordPressHealth();
    }
  }

  /**
   * Kiểm tra health của WordPress API
   */
  private async checkWordPressHealth(): Promise<void> {
    if (this.usePythonBackend) return; // Skip if using Python

    const now = Date.now();
    if (now - this.lastHealthCheck < this.healthCheckInterval) {
      return;
    }
    this.lastHealthCheck = now;

    try {
      const testUrl = databaseConfig.wordpressApiUrl + '/users/email/test@example.com';
      const response = await fetch(testUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        signal: AbortSignal.timeout(10000)
      });
      const contentType = response.headers.get('content-type') || '';
      const isJSON = contentType.includes('application/json');
      const healthyStatuses = [200, 401, 403, 404];
      this.wordPressHealthy = healthyStatuses.includes(response.status) && isJSON;
    } catch (error: any) {
      if (error.name === 'AbortError' || error.name === 'TimeoutError') {
        // Keep status
      } else {
        this.wordPressHealthy = false;
      }
    }
  }

  /**
   * Get active backend
   */
  private async getActiveBackend(): Promise<'python' | 'wordpress' | 'sqlite'> {
    if (this.usePythonBackend) return 'python';
    if (!this.useWordPress) return 'sqlite';

    await this.checkWordPressHealth();
    if (!this.wordPressHealthy) return 'sqlite';

    return 'wordpress';
  }

  async getUserById(id: string): Promise<User | null> {
    try {
      if (this.usePythonBackend) {
        return await pythonUserService.getUserById(id);
      }

      const backend = await this.getActiveBackend();
      if (backend === 'wordpress') {
        return await wordPressUserService.getUserById(id);
      } else {
        return await sqliteDatabaseService.getUserById(id);
      }
    } catch (error) {
      console.error('Error getting user by ID:', error);
      if (this.useWordPress && !this.usePythonBackend) {
        this.wordPressHealthy = false;
        return await sqliteDatabaseService.getUserById(id);
      }
      throw error;
    }
  }

  async getUserByEmail(email: string): Promise<User | null> {
    try {
      if (this.usePythonBackend) {
        return await pythonUserService.getUserByEmail(email);
      }

      const backend = await this.getActiveBackend();
      if (backend === 'wordpress') {
        return await wordPressUserService.getUserByEmail(email);
      } else {
        if (this.enableLocalFallback) {
          return await sqliteDatabaseService.getUserByEmail(email);
        }
        return null;
      }
    } catch (error) {
      if (this.enableLocalFallback && !this.usePythonBackend) {
        try { return await sqliteDatabaseService.getUserByEmail(email); } catch { }
      }
      return null;
    }
  }

  async updateUser(id: string, updates: Partial<User>): Promise<User> {
    try {
      if (this.usePythonBackend) {
        return await pythonUserService.updateUser(id, updates);
      }

      const backend = await this.getActiveBackend();
      if (backend === 'wordpress') {
        return await wordPressUserService.updateUser(id, updates);
      } else {
        if (this.enableLocalFallback) return await sqliteDatabaseService.updateUser(id, updates);
        throw new Error('WordPress not available');
      }
    } catch (error) {
      if (this.enableLocalFallback && !this.usePythonBackend) {
        try { return await sqliteDatabaseService.updateUser(id, updates); } catch (e) { throw e; }
      }
      throw error;
    }
  }

  async createUser(userData: Partial<User>): Promise<User> {
    try {
      // Python backend doesn't support Manual Creation via Frontend usually (handled by Google Auth)
      // But if needed, add to PythonUserService.
      if (this.usePythonBackend) {
        return await pythonUserService.createUser(userData);
      }

      const backend = await this.getActiveBackend();
      if (backend === 'wordpress') {
        // Retry logic omitted for brevity, adding simple call
        return await wordPressUserService.createUser(userData);
      } else {
        if (this.enableLocalFallback) return await sqliteDatabaseService.createUser(userData);
        throw new Error('System maintenance');
      }
    } catch (error) {
      if (this.enableLocalFallback && !this.usePythonBackend) {
        try { return await sqliteDatabaseService.createUser(userData); } catch (e) { throw e; }
      }
      throw error;
    }
  }

  async deleteUser(id: string): Promise<void> {
    if (this.usePythonBackend) return await pythonUserService.deleteUser(id);

    const backend = await this.getActiveBackend();
    if (backend === 'wordpress') return await wordPressUserService.deleteUser(id);
    else return await sqliteDatabaseService.deleteUser(id);
  }

  async getAllUsers(params?: {
    page?: number;
    limit?: number;
    role?: string;
    status?: string;
    search?: string;
    source_domain?: string;
  }): Promise<{ users: User[], total: number }> {
    if (this.usePythonBackend) return await pythonUserService.getAllUsers(params);

    const backend = await this.getActiveBackend();
    if (backend === 'wordpress') {
      const users = await wordPressUserService.getAllUsers();
      // WP service doesn't support pagination yet, simulate it or return all
      // For now, return as is but wrapped
      return { users, total: users.length };
    }
    else {
      if (this.enableLocalFallback) {
        const users = await sqliteDatabaseService.getAllUsers();
        return { users, total: users.length };
      }
      return { users: [], total: 0 };
    }
  }

  async getSystemStats(): Promise<any> {
    if (this.usePythonBackend) {
      return await pythonUserService.getUserStats();
    }
    // Fallback for other backends (client-side calc needed if not supported)
    return {
      totalUsers: 0,
      totalStudents: 0,
      totalTeachers: 0,
      totalAdmins: 0,
      pendingApprovals: 0,
      activeToday: 0
    };
  }

  async getUserProfile(userId: string): Promise<UserProfile | null> {
    if (this.usePythonBackend) return await pythonUserService.getUserProfile(userId);

    const backend = await this.getActiveBackend();
    if (backend === 'wordpress') return await wordPressUserService.getUserProfile(userId);
    else return await sqliteDatabaseService.getUserProfile(userId);
  }

  async createUserProfile(profileData: Partial<UserProfile>): Promise<UserProfile> {
    if (this.usePythonBackend) return await pythonUserService.createUserProfile(profileData);

    const backend = await this.getActiveBackend();
    if (backend === 'wordpress') return await wordPressUserService.createUserProfile(profileData);
    else return await sqliteDatabaseService.createUserProfile(profileData);
  }

  async updateUserProfile(userId: string, updates: Partial<UserProfile>): Promise<UserProfile> {
    if (this.usePythonBackend) return await pythonUserService.updateUserProfile(userId, updates);

    const backend = await this.getActiveBackend();
    if (backend === 'wordpress') return await wordPressUserService.updateUserProfile(userId, updates);
    else return await sqliteDatabaseService.updateUserProfile(userId, updates);
  }

  // Study methods
  async createStudySession(sessionData: any): Promise<any> {
    const session = { id: Date.now().toString(), ...sessionData, createdAt: new Date() };
    return session;
  }
  async updateStudySession(sessionId: string, updates: any): Promise<any> {
    return { id: sessionId, ...updates, updatedAt: new Date() };
  }
  async addStudyAction(actionData: any): Promise<void> { console.log('Study action:', actionData); }
  async updateStudyProgress(progressId: string, progressData: any): Promise<any> {
    const { userId, lessonId, ...updates } = progressData;
    if (!userId || !lessonId) return progressData;
    await sqliteDatabaseService.updateStudyProgress(userId, lessonId, updates);
    return progressData;
  }
  async getStudyProgress(userId: string): Promise<any[]> {
    const progress = await sqliteDatabaseService.getStudyProgress(userId);
    return progress ? [progress] : [];
  }
  async getUserStats(userId: string): Promise<any> {
    return { totalLessonsCompleted: 0, totalTimeSpent: 0, averageScore: 0, currentStreak: 0, longestStreak: 0 };
  }
  forceSQLite(): void { this.wordPressHealthy = false; }
  resetHealth(): void { this.wordPressHealthy = true; this.lastHealthCheck = 0; }
  forceWordPress(): void { this.wordPressHealthy = true; this.lastHealthCheck = Date.now() + 3600000; }
  async checkHealthManually(): Promise<void> {
    if (this.usePythonBackend) { console.log('Using Python'); return; }
    this.lastHealthCheck = 0;
    await this.checkWordPressHealth();
  }
  getBackendInfo(): { configured: string; active: string; healthy: boolean } {
    return {
      configured: this.usePythonBackend ? 'python' : (this.useWordPress ? 'wordpress' : 'sqlite'),
      active: this.usePythonBackend ? 'python' : (this.wordPressHealthy ? 'wordpress' : 'sqlite'),
      healthy: this.usePythonBackend ? true : this.wordPressHealthy,
    };
  }

  async updateUserRole(userId: string, role: string): Promise<User> {
    if (this.usePythonBackend) return await pythonUserService.updateUserRole(userId, role);

    const backend = await this.getActiveBackend();
    const permissions = this.getRolePermissions(role as any);

    // ✅ Auto-approve teachers when role is assigned
    const updates: Partial<User> = {
      role: role as any,
      permissions
    };

    // If assigning teacher role, auto-approve
    if (role === 'teacher') {
      const result = await this.getUserById(userId);
      // Get current user to preserve existing teacherInfo
      const currentUser = result;
      updates.teacherInfo = {
        ...(currentUser?.teacherInfo || {}),
        teacherId: currentUser?.teacherInfo?.teacherId || userId,
        subjects: currentUser?.teacherInfo?.subjects || [],
        experience: currentUser?.teacherInfo?.experience || 0,
        qualifications: currentUser?.teacherInfo?.qualifications || [],
        isApproved: true, // ✅ Auto-approve
        approvedAt: new Date()
      };
    }

    if (backend === 'wordpress') return await wordPressUserService.updateUser(userId, updates);
    else return await sqliteDatabaseService.updateUser(userId, updates);
  }

  // --- Lab Device Methods ---
  async getUserDevices(userId: string): Promise<any[]> {
    if (this.usePythonBackend) return await pythonUserService.getUserDevices(userId);
    return [];
  }

  async computeSecureId(mac: string): Promise<string | null> {
    if (this.usePythonBackend) return await pythonUserService.computeSecureId(mac);
    return null;
  }

  async bindDevice(userId: string, mac: string, name?: string): Promise<any> {
    if (this.usePythonBackend) return await pythonUserService.bindDevice(userId, mac, name);
    throw new Error('Not supported on this backend');
  }

  async unbindDevice(deviceId: number): Promise<void> {
    if (this.usePythonBackend) return await pythonUserService.unbindDevice(deviceId);
  }

  private getRolePermissions(role: 'student' | 'teacher' | 'admin'): string[] {
    const map = {
      student: ['lessons:read', 'quizzes:read', 'quizzes:submit', 'profile:read', 'profile:write', 'progress:read'],
      teacher: ['lessons:read', 'lessons:write', 'lessons:delete', 'quizzes:read', 'quizzes:write', 'students:read', 'profile:read', 'profile:write', 'progress:read'],
      admin: [
        'lessons:read', 'lessons:write', 'lessons:delete',
        'quizzes:read', 'quizzes:write', 'quizzes:delete',
        'users:read', 'users:write', 'users:delete', 'users:manage',
        'teachers:approve',
        'students:read', 'students:manage',
        'profile:read', 'profile:write',
        'progress:read', 'progress:manage',
        'reports:read', 'reports:write',
        'system:manage'
      ]
    };
    return map[role] || map.student;
  }
}

export const userService = new UserService();
export default userService;
